var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as crypto from 'crypto';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class PanasonicProjectorTelnet {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.hashCode = '';
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QSH`, /00(\d+)/i, tags);
                return parseInt(match[1], 10) === 1;
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00OSH:${blank ? '1' : '0'}`, /00OSH:(\d+)/i, tags);
                return parseInt(match[1], 10) === 1;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QVX:SVRS0`, /SVRS0=(.+)/i, tags);
                return match[1];
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QPW`, /0000(\d)|ERR\d+/i, tags);
                return match[1] ? match[1] === '1' : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00P${power ? 'ON' : 'OF'}`, /00PO([N|F])/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 6000);
                return match[1] === 'N';
            }),
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QSN`, /(.+)/i, tags);
                return match[1];
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QIN`, /00(...)/i, tags);
                return match[1];
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 3000);
                const match = yield this.socket.send(`${this.hashCode}00IIS:${code}`, /00IIS:(...)/i, tags, { sendTimeoutMs: 4500 });
                return match[1];
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QAV`, /(\d+)/, tags);
                return parseInt(match[1], 10);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00AVL:${volume.toString().padStart(3, '0')}`, /00AVL:(\d+)/, tags);
                return parseInt(match[1], 10);
            }),
            range: { max: 63, min: 0, round: 1 },
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00QMT`, /00(\d+)/, tags);
                return parseInt(match[1], 10) === 1;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`${this.hashCode}00AMT:${mute ? '1' : '0'}`, /00AMT:(\d+)/i, tags);
                return parseInt(match[1], 10) === 1;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                controls: ['displayBlank', 'power', 'videoInput', 'volume', 'volumeMute'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            this.hashCode = '';
            const initSteps = [
                {
                    errorCode: 'verifyFailed',
                    next: (command) => {
                        var _a, _b;
                        const regex = /NTCONTROL \d\s*([0-9a-f]*)/i;
                        const match = command.match(regex);
                        if (!match)
                            return false;
                        if (match[1]) {
                            const onConnectCode = match[1];
                            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'admin1';
                            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : 'panasonic';
                            const hashInput = `${username}:${password}:${onConnectCode}`;
                            this.hashCode = crypto.createHash('md5').update(hashInput).digest('hex');
                        }
                        return true;
                    },
                },
            ];
            let rv = yield CommandSocketInitializer.initialize(this.socket, initSteps, credentials);
            if (rv === 'connected' && this.hashCode !== '') {
                // Validate the hashCode here because the updated value of this.hashCode was not
                // available in a second step in the initSteps.
                //
                // Note from Steve: It should be available. You may just need to bind the functions.
                try {
                    const match = yield this.socket.send(`${this.hashCode}00QPW`, /(\d+|ERR)/i, [
                        'initialize.authVerify',
                    ]);
                    if (match[1] === 'ERR')
                        rv = 'authFailed';
                }
                catch (error) {
                    rv = 'authFailed';
                }
            }
            return rv;
        });
    }
}
// static properties
PanasonicProjectorTelnet.driverType = 'socketDriver';
PanasonicProjectorTelnet.key = '-LWVuHiNZ3VqgYQi8LZ3';
PanasonicProjectorTelnet.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r'],
    },
    sendTerminator: '\r',
};
PanasonicProjectorTelnet.socketOptions = [
    Object.assign(Object.assign({}, PanasonicProjectorTelnet.baseSocketOptions), { port: 1024 }),
];
